/// <amd-module name="VSS/Features/PageStateHistoryService/PageStateHistoryService" />
import { IObservableValue } from "VSS/Core/Observable";
import { IVssPageContext, ServiceOptions, Services, VssService } from "VSS/Platform/Context";
import { INavigationStateChangedEvent, IVssHistoryService } from "VSS/Platform/History";
import { Filter, FilterOperatorType, FILTER_APPLIED_EVENT, FILTER_CHANGE_EVENT, IFilterState } from "VSSUI/Utilities/Filter";
import {
    HistoryBehavior,
    IPageHistoryDelta,
    IPageHistoryFilter,
    IPageHistoryFilterField,
    IPageHistoryObservable,
    IPageHistoryStateDelta,
    IPageHistoryValueDelta,
    IPageStateHistoryService
} from "./PageStateHistoryService.types";

export class PageStateHistoryService extends VssService implements IPageStateHistoryService {
    private historyService: IVssHistoryService;
    private maintainedFilters: { [key: string]: IPageHistoryFilter } = {};
    private maintainedObservables: { [key: string]: IPageHistoryObservable<any> } = {};
    private changeQueue: IPageHistoryDelta[] = [];
    private commitChangesTimeoutId: number | undefined = undefined;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        // Hook the History service to catch the new state on browser navigate and update observables
        this.historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        this.historyService.subscribe(this.onNavigate);

        // We want to know when the page begins processing an FPS event so we can clear out observables
        document.body.addEventListener("fpsLoading", this.onFPS);
    }

    public _serviceEnd() {
        this.historyService.unsubscribe(this.onNavigate);
    }

    public getProjectedState() {
        let state = this.historyService.getState();

        for (const change of this.changeQueue) {
            state = this.generateNextState(change, state);
        }

        return state;
    }

    public queueState(historyState: { [key: string]: string }, historyBehavior: HistoryBehavior) {
        const change: IPageHistoryStateDelta = { state: historyState, historyBehavior: historyBehavior };
        this.enqueueChange(change);
    }

    public queueValueChange(key: string, value: string, historyBehavior: HistoryBehavior) {
        const change: IPageHistoryValueDelta = { key: key, newValue: value, historyBehavior: historyBehavior };
        this.enqueueChange(change);
    }

    public registerFilter(filter: Filter, key: string, filterFields: IPageHistoryFilterField<any>[]): void {
        if (this.maintainedFilters[key]) {
            $DEBUG &&
                console.warn(
                    `PageStateHistoryService attempted to register a filter with key ${key}, but was already tracking one with that key - will not register filter`
                );
            return;
        }

        // Populate filter with data from history service if it exists
        this.applyHistoryStateToFilter(this.historyService.getState(), filter, key, filterFields);

        // Queue changes to populate URL; will perform some extra logic on params that got set above
        this.applyFilterStateToHistory(filter, key, filterFields, HistoryBehavior.replace);

        // Subscribe to changes on the filter
        filter.subscribe(
            (filterState: IFilterState) => this.onFilterChange(filterState, key),
            filter.usesApplyMode() ? FILTER_APPLIED_EVENT : FILTER_CHANGE_EVENT
        );

        this.maintainedFilters[key] = {
            filter: filter,
            filterFields: filterFields,
            key: key
        };
    }

    public registerObservable<T>(
        observable: IObservableValue<T>,
        key: string,
        historyBehavior: HistoryBehavior | ((value: string) => HistoryBehavior),
        mappingFunction?: (value: T) => string,
        parsingFunction?: (navValue: string) => T
    ): void {
        if ((!mappingFunction || !parsingFunction) && typeof observable.value !== "string") {
            $DEBUG &&
                console.warn(
                    `PageStateHistoryService recieved a non-string observable with no mapping or parsing function for key ${key} while both are required - will not register observable`
                );
            return;
        }

        if (this.maintainedObservables[key]) {
            $DEBUG &&
                console.warn(
                    `PageStateHistoryService attempted to register an observable with key ${key}, but was already tracking one with that key - will not register observable`
                );
            return;
        }

        const currentState = this.historyService.getState();

        if (currentState[key]) {
            // Assign the proper value to the observable if possible
            const currentValue = parsingFunction ? parsingFunction(currentState[key]) : currentState[key];
            const observableValue = (observable as unknown) as IObservableValue<unknown>;
            if (observableValue.value !== currentValue) {
                observableValue.value = currentValue;
            }
        } else {
            // Queue a replace immediately if the value isn't already populated
            const newValue = mappingFunction ? mappingFunction(observable.value) : ((observable.value as unknown) as string);
            if (newValue) {
                this.enqueueChange({
                    historyBehavior: HistoryBehavior.replace,
                    key: key,
                    // Have to map to unknown first or TypeScript complains; safe via the typeof check above
                    newValue: newValue
                });
            }
        }

        observable.subscribe((value: T) => this.onObservableChange(value, key));

        this.maintainedObservables[key] = {
            key: key,
            observable: observable,
            historyBehavior: historyBehavior,
            mappingFunction: mappingFunction,
            parsingFunction: parsingFunction
        };
    }

    public unregisterFilter(key: string) {
        if (this.maintainedFilters[key]) {
            this.maintainedFilters[key].filter.unsubscribe((filterState: IFilterState) => this.onFilterChange(filterState, key), FILTER_CHANGE_EVENT);
            delete this.maintainedFilters[key];
        } else if ($DEBUG) {
            console.warn(`PageStateHistoryService attempted to un-register filter with key ${key} but was unable to find a match`);
        }
    }

    public unregisterObservable(key: string) {
        if (this.maintainedObservables[key]) {
            this.maintainedObservables[key].observable.unsubscribe((value: any) => this.onObservableChange(value, key));
            delete this.maintainedObservables[key];
        } else if ($DEBUG) {
            console.warn(`PageStateHistoryService attempted to un-register observable with key ${key} but was unable to find a match`);
        }
    }

    private applyFilterStateToHistory(
        filter: Filter,
        filterKey: string,
        filterFields: IPageHistoryFilterField<any>[],
        historyBehaviorOverride?: HistoryBehavior
    ) {
        const filterState = filter.getState();

        for (const filterField of filterFields) {
            const filterStateKey = filterField.filterKey;
            const fieldHistoryBehavior = historyBehaviorOverride || filterField.historyBehavior;
            let stateItemAtKey = filterState[filterStateKey];

            if (!!stateItemAtKey) {
                stateItemAtKey = stateItemAtKey!;

                const valueAtKey = stateItemAtKey.value;
                const historyKey = `${filterKey}${filterStateKey}`;

                switch (filterField.dataType) {
                    case "string":
                        this.queueValueChange(historyKey, valueAtKey, fieldHistoryBehavior);
                        break;
                    case "boolean":
                    case "number":
                        this.queueValueChange(historyKey, `${valueAtKey}`, fieldHistoryBehavior);
                        break;
                    case "object":
                        this.queueValueChange(historyKey, filterField.mappingFunction(valueAtKey), fieldHistoryBehavior);
                        break;
                }

                if (stateItemAtKey.operator) {
                    this.queueValueChange(`${historyKey}_Op`, stateItemAtKey.operator, fieldHistoryBehavior);
                }
            } else {
                this.queueValueChange(`${filterKey}${filterStateKey}`, "", fieldHistoryBehavior);
                this.queueValueChange(`${filterKey}${filterStateKey}_Op`, "", HistoryBehavior.replace);
            }
        }
    }

    private applyHistoryStateToFilter(
        historyState: { [key: string]: string },
        filter: Filter,
        key: string,
        filterFields: IPageHistoryFilterField<any>[]
    ) {
        const newFilterState: IFilterState = filter.getState();
        let pendingChanges = false;

        for (const filterItem of filterFields) {
            const historyKey = `${key}${filterItem.filterKey}`;
            const historyOpKey = `${key}${filterItem.filterKey}_Op`;

            if (historyState[historyKey]) {
                if (newFilterState[filterItem.filterKey] && newFilterState[filterItem.filterKey] !== null) {
                    newFilterState[filterItem.filterKey]!.value = this.mapStringToFilterItemValue(historyState[historyKey], filterItem);
                } else {
                    newFilterState[filterItem.filterKey] = { value: this.mapStringToFilterItemValue(historyState[historyKey], filterItem) };
                }

                pendingChanges = true;
            }

            if (historyState[historyOpKey]) {
                if (newFilterState[filterItem.filterKey] && newFilterState[filterItem.filterKey] !== null) {
                    newFilterState[filterItem.filterKey]!.operator = historyState[historyOpKey] as FilterOperatorType;
                } else {
                    newFilterState[filterItem.filterKey] = { operator: historyState[historyOpKey] as FilterOperatorType, value: "" };
                }

                pendingChanges = true;
            }
        }

        if (pendingChanges) {
            // Update the filter if there's changes to apply from the URL parameter
            filter.setState(newFilterState);
            if (filter.usesApplyMode()) {
                filter.applyChanges();
            }
        }
    }

    private mapStringToFilterItemValue(historyStateValue: string, filterItem: IPageHistoryFilterField<any>): any {
        switch (filterItem.dataType) {
            case "string":
                return historyStateValue;
            case "number":
                return Number(JSON.parse(historyStateValue));
            case "boolean":
                return Boolean(JSON.parse(historyStateValue));
            case "object":
                return filterItem.parsingFunction(historyStateValue);
        }
    }

    private commitChanges = () => {
        while (this.changeQueue.length > 0) {
            let nextState = this.historyService.getState();
            let headElement = this.changeQueue.shift()!;
            const appliedHistoryBehavior = headElement.historyBehavior;

            nextState = this.generateNextState(headElement, nextState);

            // Batch replacement changes
            while (this.changeQueue.length > 0 && this.changeQueue[0].historyBehavior === HistoryBehavior.replace) {
                headElement = this.changeQueue.shift()!;
                nextState = this.generateNextState(headElement, nextState);
            }

            switch (appliedHistoryBehavior) {
                case HistoryBehavior.newEntry:
                    this.historyService.pushState(nextState);
                    break;
                case HistoryBehavior.replace:
                    this.historyService.replaceState(nextState);
                    break;
                default:
                    $DEBUG &&
                        console.error(
                            "PageStateHistoryService recieved a change event for an element with an unknown HistoryBehavior - no change will be processed"
                        );
                    return;
            }
        }

        // Clear timeout id to allow for another use
        this.commitChangesTimeoutId = undefined;
    };

    private enqueueChange(delta: IPageHistoryDelta) {
        this.changeQueue.push(delta);

        if (!this.commitChangesTimeoutId) {
            this.commitChangesTimeoutId = setTimeout(this.commitChanges, 0);
        }
    }

    private generateNextState(delta: IPageHistoryDelta, state: { [key: string]: string }): { [key: string]: string } {
        if ("newValue" in delta) {
            // Copy changes to existing state and return a new object
            delta = delta as IPageHistoryValueDelta;
            const newState: { [key: string]: string } = {};
            Object.assign(newState, state);
            if (delta.newValue !== "") {
                newState[delta.key] = delta.newValue;
            } else {
                delete newState[delta.key];
            }
            return newState;
        } else {
            // Overwrite with this state
            delta = delta as IPageHistoryStateDelta;
            return delta.state;
        }
    }

    private onFilterChange = (filterState: IFilterState, key: string) => {
        const filter = this.maintainedFilters[key];
        this.applyFilterStateToHistory(filter.filter, filter.key, filter.filterFields);
    };

    private onFPS = () => {
        for (const key in this.maintainedObservables) {
            this.unregisterObservable(key);
        }

        for (const key in this.maintainedFilters) {
            this.unregisterFilter(key);
        }
    };

    private onNavigate = (popStateEvent: INavigationStateChangedEvent) => {
        const newState = popStateEvent.newState;

        if (!newState) {
            return;
        }

        for (const key in this.maintainedObservables) {
            const stateValue = newState.state[key] || "";
            const targetObservable = this.maintainedObservables[key];
            const newValue = targetObservable.parsingFunction ? targetObservable.parsingFunction(stateValue) : stateValue;
            if (targetObservable.observable.value !== newValue) {
                targetObservable.observable.value = newValue;
            }
        }

        for (const key in this.maintainedFilters) {
            const targetFilter = this.maintainedFilters[key];
            this.applyHistoryStateToFilter(newState.state, targetFilter.filter, targetFilter.key, targetFilter.filterFields);
        }
    };

    private onObservableChange = (newValue: any, key: string) => {
        const observable = this.maintainedObservables[key];

        if (!observable) {
            if ($DEBUG) {
                console.warn(
                    `PageStateHistory recieved a change event for observable ${key} but was unable to find a matching observable - no update will be performed`
                );
            }

            return;
        }

        const newParsedValue = observable.mappingFunction ? observable.mappingFunction(newValue) : (newValue as string);
        const currentState = this.historyService.getState();
        const currentValue = (currentState && currentState[key]) || "";
        if (currentValue !== (newParsedValue || "")) {
            const historyBehavior: HistoryBehavior =
                typeof observable.historyBehavior === "function" ? observable.historyBehavior(newValue) : observable.historyBehavior;
            this.enqueueChange({
                historyBehavior: historyBehavior,
                key: key,
                newValue: newParsedValue
            });
        }
    };
}

Services.add("IPageStateHistoryService", { serviceFactory: PageStateHistoryService, options: ServiceOptions.Persistant });
